import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert } from "lucide-react";

import * as authApi from "@/api/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { MfaChallenge } from "./MfaChallenge";
import { MfaEnrol } from "./MfaEnrol";

const schema = z.object({
  username: z.string().min(1, "Requerido"),
  password: z.string().min(1, "Requerido"),
  tenantSlug: z.string().optional(),
});
type LoginValues = z.infer<typeof schema>;

type Stage =
  | { kind: "form" }
  | { kind: "mfa"; mfaSession: string }
  | { kind: "enrol"; mfaSession: string };

export function LoginPage() {
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const { refresh } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionExpired = params.get("session") === "expired";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { tenantSlug: "" },
  });

  useEffect(() => {
    if (sessionExpired) toast.push("Tu sesión expiró. Vuelve a iniciar.", "warning");
  }, [sessionExpired, toast]);

  async function onSubmit(values: LoginValues) {
    try {
      const result = await authApi.login(values);
      if ("mfaRequired" in result && result.mfaRequired) {
        setStage(
          result.mustEnrol
            ? { kind: "enrol", mfaSession: result.mfaSession }
            : { kind: "mfa", mfaSession: result.mfaSession }
        );
      } else {
        await refresh();
        nav("/dashboard", { replace: true });
      }
    } catch {
      toast.push("Credenciales inválidas", "error");
    }
  }

  if (stage.kind === "mfa") {
    return (
      <MfaChallenge
        mfaSession={stage.mfaSession}
        onSuccess={async () => {
          await refresh();
          nav("/dashboard", { replace: true });
        }}
        onCancel={() => setStage({ kind: "form" })}
      />
    );
  }
  if (stage.kind === "enrol") {
    return (
      <MfaEnrol
        mfaSession={stage.mfaSession}
        onSuccess={async () => {
          await refresh();
          nav("/dashboard", { replace: true });
        }}
        onCancel={() => setStage({ kind: "form" })}
      />
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-center bg-gradient-to-br from-primary-600 to-info p-12">
        <div className="max-w-md text-white">
          <ShieldAlert size={56} className="mb-4" />
          <h1 className="text-4xl font-bold">ARGOS</h1>
          <p className="mt-3 text-white/80">
            Plataforma de monitoreo y recuperación vehicular en tiempo real.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="card w-full max-w-sm space-y-4"
          data-testid="login-form"
        >
          <h2 className="text-xl font-semibold">Iniciar sesión</h2>
          <div className="space-y-1">
            <label htmlFor="tenantSlug" className="text-xs text-muted-200">
              Operadora (slug)
            </label>
            <input
              id="tenantSlug"
              data-testid="login-tenant"
              className="input"
              placeholder="ocra"
              {...register("tenantSlug")}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="username" className="text-xs text-muted-200">
              Usuario
            </label>
            <input
              id="username"
              data-testid="login-username"
              autoComplete="username"
              className="input"
              {...register("username")}
            />
            {errors.username && (
              <p className="text-xs text-danger">{errors.username.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <label htmlFor="password" className="text-xs text-muted-200">
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              data-testid="login-password"
              autoComplete="current-password"
              className="input"
              {...register("password")}
            />
            {errors.password && (
              <p className="text-xs text-danger">{errors.password.message}</p>
            )}
          </div>
          <button
            type="submit"
            data-testid="login-submit"
            disabled={isSubmitting}
            className="btn-primary w-full"
          >
            {isSubmitting ? "Iniciando…" : "Iniciar sesión"}
          </button>
        </form>
      </div>
    </div>
  );
}
